import { useEffect } from "react";
import { checkIfPendingEtsConversionReady } from "../etsFunctions/etsConvertViaServer";
import { checkIfBackofficeOnline } from "../etsFunctions/etsFunctions";
import { ExportState } from "../models/ProjectMetadata";
import Logger from "../Logger";
import { showErrToast } from "../components/Toasts";
import { serverConvertionMessages } from "../etsFunctions/etsConvertViaServer";
import { itx } from "../translationItx/itx";
import universalstorageHandler from "../persistence/UniversalstorageHandler";

const logger = new Logger();

const ETS_CONVERSION_TIME_LIMIT = 30 * 60000;

// States that mean an active or recent conversion is in progress
const ACTIVE_STATES = [
  ExportState.WaitingForConversion,
  ExportState.WaitingForDownload,
  ExportState.ConversionIsRunning,
];

export function useEtsConversionStatus() {
  useEffect(() => {
    // On mount: if a conversion state was left over from a previous session
    // and its timestamp is already expired, reset silently and persist so
    // it never re-triggers on the next reload.
    (async () => {
      const s = state.projectMetadata.exportState;
      if (
        ACTIVE_STATES.includes(s) &&
        state.projectMetadata.uploadedToServerForConversion &&
        Date.now() - new Date(state.projectMetadata.uploadedToServerForConversion).getTime() > ETS_CONVERSION_TIME_LIMIT
      ) {
        state.projectMetadata.exportState = ExportState.None;
        state.projectMetadata.uploadedToServerForConversion = undefined;
        state.projectMetadata.raiseOnChange("exportState");
        try {
          await universalstorageHandler.saveProjectMetaData(state.projectMetadata);
        } catch (ex) {
          logger.error("Failed to persist stale export-state reset", ex);
        }
      }
    })();

    let isFirstRun = true;

    const id = setInterval(async () => {
      if (isFirstRun) {
        isFirstRun = false;
        return;
      }

      await updateConversionState();
    }, 3000);

    return () => clearInterval(id);
  }, []);

  async function updateConversionState() {
    try {
      const exportState = state.projectMetadata.exportState;

      // Only poll while actively converting. WaitingForDownload means the server
      // already reported Success — we must NOT downgrade it back if the server
      // subsequently returns InProgress/Processing (e.g. after the file was
      // downloaded and removed from the queue).
      if (exportState === ExportState.WaitingForConversion || exportState === ExportState.ConversionIsRunning) {
        const status = await checkIfPendingEtsConversionReady();

        // status values:
        //   Success: project ready for download
        //   InProgress: project in xchangeQueue
        //   Processing: is currently in  conversion

        switch (status) {
          case "Success":
            state.projectMetadata.exportState = ExportState.WaitingForDownload;
            break;

          case "Processing":
            state.projectMetadata.exportState = ExportState.ConversionIsRunning;
            break;

          case "InProgress":
            state.projectMetadata.exportState = ExportState.WaitingForConversion;
            break;
        }

        state.projectMetadata.raiseOnChange("exportState");
        await checkIfBackofficeOnline();

        // timeout handling — only relevant while still converting
        if (
          state.projectMetadata.uploadedToServerForConversion &&
          Date.now() - new Date(state.projectMetadata.uploadedToServerForConversion).getTime() >
            ETS_CONVERSION_TIME_LIMIT
        ) {
          state.projectMetadata.exportState = ExportState.None;
          // Clear the timestamp and persist so the stale state doesn't
          // re-appear the next time this project is loaded.
          state.projectMetadata.uploadedToServerForConversion = undefined;
          state.projectMetadata.raiseOnChange("exportState");
          showErrToast(serverConvertionMessages.timeout);
          try {
            await universalstorageHandler.saveProjectMetaData(state.projectMetadata);
          } catch (ex) {
            logger.error("Failed to persist timeout export-state reset", ex);
          }
        }
      }
    } catch (ex) {
      logger.error(ex);
    }
  }

  return {
    updateConversionState, // optional manual trigger
  };
}
